/**
 * Org MCP API Keys page — B2B org-level MCP key management (b2b-1-3)
 *
 * Route: /org/settings/mcp-keys
 *
 * Features:
 * - Generate org MCP API key (omcp_ prefix) — key shown once only
 * - List existing keys with label, prefix, last used date
 * - Revoke keys
 * - Copy setup snippet for Claude Desktop / Cursor
 * - Rate limit info: 60 calls/min per key
 */

import React, { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { Copy, Check, Key, Trash2, Plus, AlertTriangle, Building2, Zap } from 'lucide-react';
import { orgMcpApi, OrgMcpKeyItem, OrgMcpKeyCreated } from '../../services/orgMcpApi';

const MCP_SSE_URL = 'https://invest.marome-investments.co.za/mcp/sse';

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

function CopyButton({ text }: { text: string }) {
  const [copied, setCopied] = useState(false);
  const handleCopy = async () => {
    await navigator.clipboard.writeText(text);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };
  return (
    <button
      onClick={handleCopy}
      className="inline-flex items-center gap-1 px-2 py-1 text-xs rounded bg-slate-700 hover:bg-slate-600 text-slate-300 transition-colors"
    >
      {copied ? <Check size={12} className="text-emerald-400" /> : <Copy size={12} />}
      {copied ? 'Copied' : 'Copy'}
    </button>
  );
}

function OrgConfigSnippet({ apiKey }: { apiKey: string }) {
  const [activeTab, setActiveTab] = useState<'claude' | 'cursor' | 'claude-code'>('claude');

  const snippet = JSON.stringify({
    mcpServers: {
      'my-investments-org': {
        url: MCP_SSE_URL,
        headers: { Authorization: `Bearer ${apiKey}` },
      },
    },
  }, null, 2);

  const codeSnippet = `mcp add my-investments-org ${MCP_SSE_URL} \\
  --header "Authorization: Bearer ${apiKey}"`;

  const tabs = [
    { id: 'claude' as const, label: 'Claude Desktop' },
    { id: 'cursor' as const, label: 'Cursor / Windsurf' },
    { id: 'claude-code' as const, label: 'Claude Code' },
  ];

  const content = activeTab === 'claude-code' ? codeSnippet : snippet;

  return (
    <div className="mt-4">
      <p className="text-sm font-medium text-slate-300 mb-3">Setup snippet</p>
      <div className="flex gap-1 mb-3">
        {tabs.map((t) => (
          <button
            key={t.id}
            onClick={() => setActiveTab(t.id)}
            className={`px-3 py-1.5 text-xs rounded-md transition-colors ${
              activeTab === t.id
                ? 'bg-cyan-600 text-white'
                : 'bg-slate-700 text-slate-400 hover:bg-slate-600'
            }`}
          >
            {t.label}
          </button>
        ))}
      </div>
      <div className="relative bg-slate-900 rounded-lg p-4">
        <pre className="text-xs text-emerald-300 overflow-x-auto whitespace-pre-wrap break-all">
          {content}
        </pre>
        <div className="absolute top-2 right-2">
          <CopyButton text={content} />
        </div>
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Key row component
// ---------------------------------------------------------------------------

function KeyRow({ keyItem, onRevoke }: { keyItem: OrgMcpKeyItem; onRevoke: () => void }) {
  const [confirming, setConfirming] = useState(false);

  const lastUsed = keyItem.last_used_at
    ? new Date(keyItem.last_used_at).toLocaleString()
    : 'Never';

  const createdAt = new Date(keyItem.created_at).toLocaleDateString();

  return (
    <div className="flex items-center justify-between py-3 px-4 bg-slate-800 rounded-lg border border-slate-700">
      <div className="flex items-center gap-3">
        <Key size={16} className="text-cyan-400 shrink-0" />
        <div>
          <div className="flex items-center gap-2">
            <span className="font-mono text-sm text-slate-200">
              omcp_{keyItem.key_prefix}…
            </span>
            {keyItem.label && (
              <span className="text-xs text-slate-400">— {keyItem.label}</span>
            )}
          </div>
          <div className="text-xs text-slate-500 mt-0.5">
            Created {createdAt} · Last used: {lastUsed}
          </div>
        </div>
      </div>

      <div>
        {confirming ? (
          <div className="flex items-center gap-2">
            <span className="text-xs text-amber-400">Revoke?</span>
            <button
              onClick={onRevoke}
              className="px-2 py-1 text-xs rounded bg-red-600 hover:bg-red-500 text-white transition-colors"
            >
              Yes
            </button>
            <button
              onClick={() => setConfirming(false)}
              className="px-2 py-1 text-xs rounded bg-slate-700 hover:bg-slate-600 text-slate-300 transition-colors"
            >
              No
            </button>
          </div>
        ) : (
          <button
            onClick={() => setConfirming(true)}
            className="p-1.5 rounded hover:bg-slate-700 text-slate-400 hover:text-red-400 transition-colors"
            title="Revoke key"
          >
            <Trash2 size={14} />
          </button>
        )}
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// New key banner (shown once after generation)
// ---------------------------------------------------------------------------

function NewKeyBanner({
  keyData,
  onDismiss,
}: {
  keyData: OrgMcpKeyCreated;
  onDismiss: () => void;
}) {
  return (
    <div className="rounded-xl border border-emerald-500/40 bg-emerald-900/20 p-5 space-y-3">
      <div className="flex items-start gap-3">
        <AlertTriangle size={18} className="text-amber-400 mt-0.5 shrink-0" />
        <div>
          <p className="text-sm font-semibold text-emerald-300">
            Key generated — save it now
          </p>
          <p className="text-xs text-slate-400 mt-0.5">
            This key will not be shown again. Copy it before dismissing.
          </p>
        </div>
      </div>

      <div className="flex items-center gap-3 bg-slate-900 rounded-lg px-4 py-2.5">
        <code className="font-mono text-sm text-emerald-300 flex-1 break-all">
          {keyData.key}
        </code>
        <CopyButton text={keyData.key} />
      </div>

      <OrgConfigSnippet apiKey={keyData.key} />

      <button
        onClick={onDismiss}
        className="w-full mt-2 py-2 text-sm rounded-lg bg-slate-700 hover:bg-slate-600 text-slate-300 transition-colors"
      >
        I've saved my key — dismiss
      </button>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Main page
// ---------------------------------------------------------------------------

export default function OrgMcpKeysPage() {
  const queryClient = useQueryClient();
  const [newKeyLabel, setNewKeyLabel] = useState('');
  const [newKeyData, setNewKeyData] = useState<OrgMcpKeyCreated | null>(null);
  const [generateError, setGenerateError] = useState<string | null>(null);

  const { data: keys = [], isLoading, error: loadError } = useQuery<OrgMcpKeyItem[]>({
    queryKey: ['org-mcp-keys'],
    queryFn: () => orgMcpApi.listKeys(),
    retry: false,
  });

  const generateMutation = useMutation({
    mutationFn: (label: string) => orgMcpApi.generateKey(label || undefined),
    onSuccess: (data) => {
      setNewKeyData(data);
      setNewKeyLabel('');
      setGenerateError(null);
      queryClient.invalidateQueries({ queryKey: ['org-mcp-keys'] });
    },
    onError: (err: Error) => {
      setGenerateError(err.message);
    },
  });

  const revokeMutation = useMutation({
    mutationFn: (keyId: number) => orgMcpApi.revokeKey(keyId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['org-mcp-keys'] });
    },
  });

  const handleGenerate = (e: React.FormEvent) => {
    e.preventDefault();
    generateMutation.mutate(newKeyLabel);
  };

  return (
    <div className="max-w-2xl mx-auto space-y-6">
      {/* Header */}
      <div className="flex items-center gap-3">
        <Building2 size={22} className="text-cyan-400" />
        <div>
          <h1 className="text-xl font-bold text-white">Organisation MCP API Keys</h1>
          <p className="text-sm text-slate-400 mt-0.5">
            Generate shared API keys for your organisation's AI tooling integrations.
            Keys are scoped to your org's allowed exchanges.
          </p>
        </div>
      </div>

      {/* Rate limit info */}
      <div className="flex items-center gap-2 px-4 py-2.5 rounded-lg bg-slate-800 border border-slate-700 text-xs text-slate-400">
        <Zap size={13} className="text-amber-400 shrink-0" />
        <span>Rate limit: 60 requests per minute per key. Data is scoped to your org's allowed exchanges.</span>
      </div>

      {/* New key banner */}
      {newKeyData && (
        <NewKeyBanner
          keyData={newKeyData}
          onDismiss={() => setNewKeyData(null)}
        />
      )}

      {/* Generate form */}
      {!newKeyData && (
        <div className="rounded-xl border border-slate-700 bg-slate-800/50 p-5">
          <h2 className="text-sm font-semibold text-slate-200 mb-3">Generate new key</h2>
          <form onSubmit={handleGenerate} className="flex gap-3">
            <input
              type="text"
              value={newKeyLabel}
              onChange={(e) => setNewKeyLabel(e.target.value)}
              placeholder="Label (e.g. CI pipeline)"
              maxLength={100}
              className="flex-1 px-3 py-2 text-sm rounded-lg bg-slate-900 border border-slate-600 text-slate-200 placeholder-slate-500 focus:outline-none focus:border-cyan-500"
            />
            <button
              type="submit"
              disabled={generateMutation.isPending || keys.length >= 10}
              className="inline-flex items-center gap-2 px-4 py-2 text-sm font-medium rounded-lg bg-cyan-600 hover:bg-cyan-500 disabled:opacity-50 disabled:cursor-not-allowed text-white transition-colors"
            >
              <Plus size={14} />
              {generateMutation.isPending ? 'Generating…' : 'Generate'}
            </button>
          </form>
          {keys.length >= 10 && (
            <p className="text-xs text-amber-400 mt-2">
              Maximum of 10 keys per organisation reached. Revoke unused keys to create new ones.
            </p>
          )}
          {generateError && (
            <p className="text-xs text-red-400 mt-2">{generateError}</p>
          )}
        </div>
      )}

      {/* Key list */}
      <div>
        <h2 className="text-sm font-semibold text-slate-200 mb-3">
          Active keys{' '}
          <span className="text-slate-500 font-normal">({keys.length}/10)</span>
        </h2>

        {isLoading && (
          <p className="text-sm text-slate-400 py-4 text-center">Loading keys…</p>
        )}

        {loadError && (
          <div className="flex items-center gap-2 text-sm text-red-400 py-4">
            <AlertTriangle size={14} />
            <span>
              {(loadError as Error).message || 'Failed to load keys. Org admin access required.'}
            </span>
          </div>
        )}

        {!isLoading && !loadError && keys.length === 0 && (
          <p className="text-sm text-slate-500 py-4 text-center">
            No keys yet. Generate one above.
          </p>
        )}

        <div className="space-y-2">
          {keys.map((k) => (
            <KeyRow
              key={k.id}
              keyItem={k}
              onRevoke={() => revokeMutation.mutate(k.id)}
            />
          ))}
        </div>
      </div>
    </div>
  );
}
